import React from 'react';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  className = '',
}: DataTableProps<T>) {
  return (
    <div
      className={`overflow-x-auto ${className}`}
      data-test-id="data-table-wrapper"
    >
      <table
        className="min-w-full divide-y divide-gray-200"
        data-test-id="data-table"
      >
        <thead className="bg-gray-50" data-test-id="data-table-header">
          <tr data-test-id="data-table-header-row">
            {columns.map((column, index) => (
              <th
                key={index}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.className || ''
                }`}
                data-test-id={`data-table-header-cell-${index}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          className="bg-white divide-y divide-gray-200"
          data-test-id="data-table-body"
        >
          {data.length === 0 ? (
            <tr data-test-id="data-table-empty-row">
              <td
                colSpan={columns.length}
                className="px-6 py-8 text-center text-gray-500"
                data-test-id="data-table-empty-message"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={keyExtractor(row, rowIndex)}
                onClick={() => onRowClick?.(row)}
                className={`${
                  onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
                data-test-id={`data-table-row-${rowIndex}`}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${
                      column.className || ''
                    }`}
                    data-test-id={`data-table-cell-${rowIndex}-${colIndex}`}
                  >
                    {typeof column.accessor === 'function'
                      ? column.accessor(row)
                      : (row[column.accessor] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
